import React from "react";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { getFeatureLabel, getFeatureIcon, getFeatureDescription, getFeatureCategory } from "src/helpers/planHelper";
import { classNames } from "src/helpers/classNames";

const categoryColors = {
  limit: "blue",
  security: "green",
  integration: "purple",
  other: "gray",
};

const PlanFeatures = ({ features }) => {
  const normalizedFeatures = Array.isArray(features)
    ? features
    : typeof features === "object" && features !== null
      ? Object.entries(features).map(([code, value]) => ({
          code,
          ...value,
        }))
      : [];

  const grouped =
    normalizedFeatures.length > 0
      ? normalizedFeatures.reduce((acc, f) => {
          const type = getFeatureCategory(f?.code) || "other";
          if (!acc[type]) acc[type] = [];
          acc[type].push(f);
          return acc;
        }, {})
      : {};

  const renderFeatureRow = (feature) => {
    const Icon = getFeatureIcon(feature?.code);
    const label = getFeatureLabel(feature?.code);
    const description = getFeatureDescription(feature?.code, feature?.value);
    const value = feature?.value;
    const isBoolean = typeof value === "boolean";
    const isEnabled = isBoolean ? value : true;
    const isUnlimited = value === "unlimited";

    return (
      <div
        key={feature?.code}
        className={classNames("flex items-start justify-between border-b border-gray-100 p-3 transition-all last:border-none", isEnabled ? "bg-white hover:bg-gray-50" : "bg-gray-50 opacity-60")}>
        <div className="flex w-full items-center gap-3">
          <div className={classNames("flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg", isEnabled ? "bg-blue-50 text-highlightColor" : "bg-gray-100 text-gray-400")}>{Icon ? <Icon className="h-6 w-6" /> : "?"}</div>
          <div className="w-full">
            <div className="flex w-full items-center justify-between gap-2">
              <span className="font-semibold text-gray-900">{label}</span>
              {!isBoolean && <span className={classNames("rounded-full px-3 py-0.5 text-xs font-semibold", isUnlimited ? "bg-gradient-to-r from-gray-100 to-pink-100 text-highlightColor" : "bg-blue-50 text-highlightColor")}>{isUnlimited ? "Unlimited" : value}</span>}
            </div>
            <div className="text-sm text-gray-600">{description}</div>
          </div>
        </div>
        {isBoolean && <div className="mt-1">{value ? <CheckCircleIcon className="h-5 w-5 text-highlightColor" /> : <XCircleIcon className="h-5 w-5 text-gray-300" />}</div>}
      </div>
    );
  };

  const renderCategory = (category, items) => (
    <div
      key={category}
      className="flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
        <div className="flex items-center gap-2">
          <div className={`h-2 w-2 rounded-full bg-${categoryColors[category] || "gray"}-600`}></div>
          <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-700">{category === "limit" ? "Limits & Quotas" : category === "security" ? "Security & Access" : "Integrations & Tools"}</h4>
        </div>
      </div>
      <div>{items.map(renderFeatureRow)}</div>
    </div>
  );

  const limitItems = grouped.limit || [];
  const securityItems = grouped.security || [];
  const otherItems = grouped.integration || grouped.other || [];

  return (
    <div className="my-6 flex flex-col gap-4 md:flex-row md:items-start">
      {limitItems.length > 0 && <div className="flex-1">{renderCategory("limit", limitItems)}</div>}
      {securityItems.length > 0 && <div className="flex-1">{renderCategory("security", securityItems)}</div>}
      {otherItems.length > 0 && <div className="flex-1">{renderCategory("integration", otherItems)}</div>}
    </div>
  );
};

export default PlanFeatures;
